import React from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { OutlinedInput } from '@mui/material';
import styles from './LogMeasurementDialog.module.scss';
import APIClient from '../../../../util/APIClient';
import { isNumeric } from '../../../../hooks/useTreeEditor';
import FormAlert from '../FormAlert/FormAlert';
import { getAbsoluteTimestamp, getRelativeTimestamp } from '../../../../util/formatDate';
import { Link, useParams } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

function logMeasurementSortFn(a, b) {
  if (a.time < b.time) {
    return -1;
  }
  if (a.time > b.time) {
    return 1;
  }
  return 0;
}

function LogMeasurementDialog({ goal, isOpen, close, reload = () => {}, path, editPath, hideEditButton = false }) {
  const [absoluteValueInput, setAbsoluteValueInput] = React.useState('');
  const [relativeValueInput, setRelativeValueInput] = React.useState('');
  const [absoluteValueError, setAbsoluteValueError] = React.useState(null);
  const [relativeValueError, setRelativeValueError] = React.useState(null);
  const [timeInput, setTimeInput] = React.useState(new Date());
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  goal.goalMeasures.sort(logMeasurementSortFn);

  const updateAbsoluteValue = React.useCallback(
    (v) => {
      setAbsoluteValueInput(v);
      if (v !== '') {
        if (!isNumeric(v)) {
          setAbsoluteValueError('Value must be a number.');
          return;
        } else {
          setAbsoluteValueError(null);
        }
        const currentGoalMeasureValue =
          goal.goalMeasures.length > 0 ? goal.goalMeasures[goal.goalMeasures.length - 1].value : goal.startValue;
        const newAbsoluteValue = parseFloat(v);
        const newRelativeValueInput = newAbsoluteValue - currentGoalMeasureValue;
        setRelativeValueInput(newRelativeValueInput.toString());
      } else {
        setRelativeValueInput('');
      }
    },
    [goal],
  );

  const updateRelativeValue = React.useCallback(
    (v) => {
      setRelativeValueInput(v);
      if (v !== '') {
        if (!isNumeric(v)) {
          setRelativeValueError('Value must be a number.');
          return;
        } else {
          setRelativeValueError(null);
        }
        const currentGoalMeasureValue =
          goal.goalMeasures.length > 0 ? goal.goalMeasures[goal.goalMeasures.length - 1].value : goal.startValue;
        const newRelativeValue = parseFloat(v);
        const newAbsoluteValueInput = currentGoalMeasureValue + newRelativeValue;
        setAbsoluteValueInput(newAbsoluteValueInput.toString());
      } else {
        setAbsoluteValueInput('');
      }
    },
    [goal],
  );

  const handleClose = () => {
    setAbsoluteValueInput('');
    setRelativeValueInput('');
    setAbsoluteValueError(null);
    setRelativeValueError(null);
    setTimeInput(new Date());
    setFormAlert(null);
    setIsSubmitting(false);
    close();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.post(path, {
        time: new Date(timeInput.setHours(12, 0, 0, 0)),
        value: absoluteValueInput,
      });
      reload();
      handleClose();
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not log measurement!',
        message:
          'Something went wrong when logging the measurement. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    handleClose();
  };

  const handleFocusLost = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleFocusLost}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" className={styles.title}>
        Log measurement
      </DialogTitle>
      <div className={styles.content}>
        <DialogContentText id="alert-dialog-description">
          You are about to update the measurement for goal
          <span style={{ fontStyle: 'italic', paddingLeft: '.2rem' }}>{goal.label}</span>.
          <br />
          To do this, either enter the value by which the goal has changed into the{' '}
          <span style={{ fontWeight: 'bold' }}>change by</span> field, providing the relative difference, or use the new
          value field to set the <span style={{ fontWeight: 'bold' }}>total value</span> for this goal. You can also
          change the date for which this value should be logged.
        </DialogContentText>
        {goal.goalMeasures.length > 0 && (
          <div className={styles.pastMeasures}>
            <div className={styles.pastMeasuresHeader}>
              <p className={styles.label}>Recent measurements:</p>
              {!hideEditButton && (
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  startIcon={<EditOutlinedIcon />}
                  component={Link}
                  to={editPath}
                >
                  Edit
                </Button>
              )}
            </div>
            <div className={styles.measures}>
              {goal.goalMeasures.length > 3 && (
                <>
                  <p>...</p>
                  <p>...</p>
                  <p>...</p>
                </>
              )}
              {goal.goalMeasures.slice(-3).map((m) => (
                <React.Fragment key={m.id}>
                  <p>{m.value}</p>
                  <p>{getAbsoluteTimestamp(new Date(m.time))}</p>
                  <p>({getRelativeTimestamp(new Date(m.time))})</p>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className={styles.form}>
          <div className={styles.row}>
            <FormControl
              sx={{ m: 1 }}
              error={!!relativeValueError}
              // disabled={!isEditable}
              className={styles.formControl}
            >
              <InputLabel id="relative-value-input-label">Change by</InputLabel>
              <OutlinedInput
                className={styles.input}
                id="relative-value-input"
                label="Change by"
                value={relativeValueInput}
                onChange={(e) => updateRelativeValue(e.target.value)}
              />
              {relativeValueError && <FormHelperText>{relativeValueError}</FormHelperText>}
            </FormControl>
            <FormControl
              sx={{ m: 1 }}
              error={!!absoluteValueError}
              // disabled={!isEditable}
              className={styles.formControl}
            >
              <InputLabel id="absolute-value-input-label">New value</InputLabel>
              <OutlinedInput
                className={styles.input}
                id="absolute-value-input"
                label="New value"
                value={absoluteValueInput}
                onChange={(e) => updateAbsoluteValue(e.target.value)}
              />
              {absoluteValueError && <FormHelperText>{absoluteValueError}</FormHelperText>}
            </FormControl>
          </div>
          <FormControl
            sx={{ m: 1 }}
            // disabled={!isEditable}
            className={styles.formControl}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                id="time-input"
                value={timeInput}
                label="Date"
                inputFormat="dd/MM/yyyy"
                format="dd/MM/yyyy"
                onChange={(value) => setTimeInput(value)}
                slotProps={{
                  textField: {
                    className: styles.input,
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </div>
        <div className={styles.buttons}>
          <Button onClick={handleAbort} color="primary" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            color="primary"
            variant="contained"
            type="submit"
            disabled={
              absoluteValueInput === '' || !!absoluteValueError || relativeValueInput === '' || !!relativeValueError
            }
            form="logMeasurementFor"
            onClick={handleSubmit}
          >
            Log measurement
          </LoadingButton>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </div>
    </Dialog>
  );
}

/**
 * Must be placed within a path that contains the teamId parameter.
 */
export function TeamLogMeasurementDialog(props) {
  const { teamId } = useParams();
  return (
    <LogMeasurementDialog
      {...props}
      path={`/user/teams/${teamId}/goals/${props.goal.id}/measures`}
      editPath={`/user/teams/${teamId}/goals/${props.goal.id}`}
    />
  );
}

export function AdministratorLogMeasurementDialog(props) {
  return (
    <LogMeasurementDialog
      {...props}
      path={`/administrator/goals/${props.goal.id}/measures`}
      editPath={`/administrator/goals/${props.goal.id}`}
    />
  );
}
